import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionsAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, Loader2, XCircle, Sparkles, 
  ArrowRight, FileText, Shield, Zap
} from 'lucide-react';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [planId, setPlanId] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Session de paiement introuvable');
      return;
    }

    if (attempts >= 10) {
      setStatus('error');
      setMessage('Délai de vérification dépassé. Veuillez vérifier votre email pour confirmation.');
      return;
    }

    try {
      const response = await subscriptionsAPI.getCheckoutStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage(data.message || 'Abonnement activé avec succès !');
        setPlanId(data.plan_id);
        return;
      }

      if (data.status === 'expired') {
        setStatus('expired');
        setMessage('La session de paiement a expiré. Veuillez réessayer.');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
      setMessage('Paramètre de session manquant');
    }
  }, [sessionId]);

  const planFeatures = {
    pro: [
      'Simulation PPF/PDP illimitée',
      'Export Factur-X conforme',
      'Scanner IA illimité',
      'Support prioritaire'
    ],
    expert: [
      'Envoi réel PPF/PDP',
      'e-Reporting TVA (disponible sept. 2026)',
      'Export comptable CSV',
      'Support dédié',
      'Factures illimitées'
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-lg border-slate-200 shadow-xl">
        <CardContent className="p-8 text-center">
          {/* Loading */}
          {status === 'loading' && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Vérification du paiement...
                </h2>
                <p className="text-slate-600">
                  Merci de patienter quelques instants
                </p>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-slate-500">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span>Tentative {attempts + 1}/10</span>
              </div>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-12 w-12 text-emerald-600" />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8">
                  <Sparkles className="h-8 w-8 text-amber-500" />
                </div>
              </div>
              
              <div>
                <Badge className="mb-3 bg-emerald-100 text-emerald-700 border-emerald-200">
                  Paiement confirmé
                </Badge>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Bienvenue dans l'abonnement {planId === 'expert' ? 'Expert' : 'Pro'} !
                </h2>
                <p className="text-slate-600">
                  {message}
                </p>
              </div>

              {/* Features unlocked */}
              {planId && planFeatures[planId] && (
                <div className="bg-slate-50 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-blue-600" />
                    Fonctionnalités débloquées
                  </p>
                  <ul className="space-y-2">
                    {planFeatures[planId].map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => navigate('/dashboard/smartfacture')}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Créer une facture
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  Tableau de bord
                </Button>
              </div>
            </div>
          )}

          {/* Error / Expired */}
          {(status === 'error' || status === 'expired') && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  {status === 'expired' ? 'Session expirée' : 'Une erreur est survenue'}
                </h2>
                <p className="text-slate-600">
                  {message}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Réessayer
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  Tableau de bord
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
